import { AxiosRequestConfig } from 'axios';
import { doorClient, ImResponse, resolve, storeHomeClient } from '.';
import chatttSDK from 'services/chattt';
import { logger } from 'services/logger';
import { getDeviceId } from 'lib/deviceId';
import { getFileHash } from 'utils/file';

export const makeUploadFileFormdata = (file: File, fileKey: string = 'file') => {
  const fd = new FormData();
  fd.append(fileKey, file);
  return fd;
};

export interface UploadResourceData {
  file: any;
  storeId?: string;
  region?: General.UpperCaseRegion;
  fileType: 'IMAGE' | 'VIDEO';
  reqSource: string;
  /**
   * 向shopee提出争议时上传的图片需要提供该参数
   * 退款单id 传此参数则上传到退款单专用接口
   */
  returnSn?: string;
  channel?: string;
  uploadResourceCentre?: boolean;
  storeGroupId?: string;
}

export interface UploadResourceResponse {
  id: string;
  url?: string;
  localUrl?: string;
  resourceState: Resource.ResourceState;
  resourceCentreId?: string;
  externalVideoId?: string;
}

export const uploadOssResource = (
  data: Pick<UploadResourceData, 'file'> & { onProgress?: (e: any) => void },
) => {
  return ossUpload({
    file: data.file,
  }).then(({ thumbUrl, videoUrl, localVideoUrl }) => {
    return {
      url: videoUrl || localVideoUrl,
      thumbUrl,
    };
  });
};

export const uploadResource = (data: UploadResourceData) => {
  if (!data.returnSn) {
    const uploadAllPlatform = data.uploadResourceCentre;
    return ossUpload({
      file: data.file,
      storeId: data.storeId,
      storeGroupId: uploadAllPlatform ? data.storeGroupId : undefined,
      region: data.region,
      uploadPlatform: Boolean(data.storeId) || data.uploadResourceCentre,
      uploadAllPlatform,
      uploadResourceCentre: data.uploadResourceCentre,
      fileType: data.fileType === 'IMAGE' ? 'image' : 'video',
    }).then(({ resourceId, videoUrl, localVideoUrl, state, externalVideoId, resourceCentreId }) => {
      return {
        id: `${resourceId}`,
        url: videoUrl,
        localUrl: localVideoUrl,
        resourceState: state,
        resourceCentreId,
        externalVideoId,
      } as UploadResourceResponse;
    });
  }

  const formData = new FormData();

  Object.keys(data).forEach((key) => {
    if (data[key]) {
      formData.append(key, data[key]);
    }
  });

  return resolve(
    doorClient.post<ImResponse<UploadResourceResponse>>(
      '/oversea-store-home/uploadResource',
      formData,
    ),
  );
};

export type ShopeeMultiAuthShopRegion = General.UpperCaseRegion | 'CNSC';

export enum ShopeeMultiAuthShopStatus {
  // 未授权
  None,
  // 本org已授权
  SameOrg,
  // 其他org已授权
  OtherOrg,
}

export interface ShopeeMultiAuthShop {
  region: ShopeeMultiAuthShopRegion;
  accountName: string;
  password: string;
  isCnsc: boolean;
  shopName: string[];
  authStatus: ShopeeMultiAuthShopStatus;
}

export const uploadMultiAuthXls = (
  file: File,
  storeType: IM_API.Admin.StoreType,
  fileKey = 'file',
  config?: AxiosRequestConfig,
) => {
  const fd = makeUploadFileFormdata(file, fileKey);
  fd.append('storeType', storeType);
  return storeHomeClient.post<
    ImResponse<{
      data: Array<ShopeeMultiAuthShop>;
    }>
  >('auth/read_shop_info_upload', fd, config);
};

/**
 * 上传日志文件到 oss
 */
export const uploadLogFile = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);
  return storeHomeClient.post('upload_log_file', formData);
};

interface UploadResponse {
  resourceId: string;
  videoUrl: string;
  localVideoUrl: string;
  thumbUrl: string;
  durationSeconds: number;
  state: Resource.ResourceState;
  externalVideoId: string;
  resourceCentreId: string;
}

const getCountryCode = () => {
  return (
    chatttSDK?.app?.getLocaleCountryCode()?.toUpperCase() || (window as any).deviceInfo?.countryCode
  );
};

const getFileType = (file: File) =>
  file.type === 'application/pdf'
    ? 'pdf'
    : file.type === 'text/plain'
    ? 'txt'
    : file.type.split('/')[0];

export const preUpload = ({
  file,
  fileHash,
  storeId,
  storeGroupId,
  uploadResourceCentre = false,
  uploadAllPlatform,
  fileType,
}: {
  file: File;
  fileHash: string;
  storeId?: string;
  storeGroupId?: string;
  uploadResourceCentre?: boolean;
  uploadAllPlatform?: boolean;
  fileType?: string;
}) => {
  const body = {
    fileName: file.name,
    fileSize: file.size,
    fileType: fileType || getFileType(file),
    fileHash,
    storeId,
    storeGroupId,
    uploadResourceCentre,
    uploadAllPlatform,
    needAccelerate: (getCountryCode() || 'CN') !== 'CN',
  };
  return resolve(
    storeHomeClient.post<
      ImResponse<
        | {
            hitCache: false;
            presignedUrl: string;
            objectKey: string;
            ticket: string;
          }
        | {
            hitCache: true;
            existingRecord: UploadResponse;
          }
      >
    >('/upload/presign-url', body),
  );
};

export const uploadByUrl = ({
  file,
  fileHash,
  uploadResourceCentre = false,
  fileType,
  ...others
}: {
  file: File;
  fileHash: string;
  uploadedUrl: string;
  objectKey: string;
  ticket: string;
  storeId?: string;
  storeGroupId?: string;
  uploadResourceCentre?: boolean;
  uploadPlatform?: boolean;
  uploadAllPlatform?: boolean;
  externalConversationId?: string;
  fileType?: string;
}) => {
  const body = {
    fileName: file.name,
    fileSize: file.size,
    fileType: fileType || getFileType(file),
    fileHash,
    uploadResourceCentre,
    ...others,
  };
  return resolve(storeHomeClient.post<ImResponse<UploadResponse>>('/upload/file-by-url', body));
};

const getFileHashByWorker = (file: File) => {
  let worker: Worker;
  try {
    worker = new Worker(new URL('../../worker/fileWorker', import.meta.url), {
      type: 'module',
    });
  } catch (_) {
    return getFileHash(file);
  }
  return new Promise<string>((resolve, reject) => {
    worker.postMessage({ type: 'getFileHash', file });
    const timer = setTimeout(() => {
      reject(new Error('Get file hash timeout'));
      worker.terminate();
    }, 120 * 1000);
    worker.onmessage = (e) => {
      if (e.data.type === 'hashResult' && e.data.hash) {
        resolve(e.data.hash);
      }
      if (e.data.type === 'hashError') {
        reject(e.data.err);
      }
      worker.terminate();
      clearTimeout(timer);
    };
    worker.onerror = () => {
      getFileHash(file).then(resolve).catch(reject);
      worker.terminate();
      clearTimeout(timer);
    };
  });
};

export const uploadLogInfoMap: any = {};

export const ossUpload = async ({
  file,
  storeId,
  storeGroupId,
  region,
  uploadResourceCentre,
  uploadPlatform,
  uploadAllPlatform,
  externalConversationId,
  fileType,
}: {
  file: File;
  storeId?: string;
  storeGroupId?: string;
  region?: General.UpperCaseRegion;
  uploadResourceCentre?: boolean;
  uploadPlatform?: boolean;
  uploadAllPlatform?: boolean;
  externalConversationId?: string;
  fileType?: string;
}) => {
  const beforeTime = Date.now();
  if (!chatttSDK && !(window as any).deviceInfo) {
    try {
      await getDeviceId(25000);
    } catch (_) {}
  }
  const getDeviceInfoDuration = Date.now() - beforeTime;
  const startTime = Date.now();
  let getHashTime: number;
  let getPreUrlTime: number;
  let uploadOssTime: number;
  return getFileHashByWorker(file)
    .then((fileHash) => {
      getHashTime = Date.now();
      const getHashDuration = getHashTime - startTime;
      return preUpload({
        file,
        fileHash,
        storeId: uploadResourceCentre ? undefined : storeId,
        storeGroupId: uploadAllPlatform ? storeGroupId : undefined,
        uploadResourceCentre,
        uploadAllPlatform,
        fileType,
      }).then((res) => {
        if (res.hitCache) {
          return {
            ...res.existingRecord,
            resourceCentreId: uploadResourceCentre
              ? res.existingRecord.resourceCentreId
              : undefined,
            state: 8,
          };
        }
        getPreUrlTime = Date.now();
        const getPreUrlDuration = getPreUrlTime - getHashTime;
        const { presignedUrl, objectKey, ticket } = res;
        const uploadOss = (retry = 2): Promise<Response> => {
          return fetch(presignedUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': 'application/octet-stream',
            },
          })
            .then((res) => {
              if (!res.ok) {
                return Promise.reject(new Error(`Upload failed, status: ${res.status}`));
              }
              return res;
            })
            .catch((err) => {
              if (retry > 0) {
                return uploadOss(retry - 1);
              }
              return Promise.reject(err);
            });
        };
        return uploadOss().then(() => {
          uploadOssTime = Date.now();
          const uploadOssDuration = uploadOssTime - getPreUrlTime;
          const { origin, pathname } = new URL(presignedUrl);
          return uploadByUrl({
            file,
            fileHash,
            uploadedUrl: `${origin}${pathname}`,
            objectKey,
            ticket,
            storeId: uploadResourceCentre ? undefined : storeId,
            storeGroupId: uploadAllPlatform ? storeGroupId : undefined,
            uploadResourceCentre,
            uploadPlatform: uploadResourceCentre ? undefined : uploadPlatform,
            uploadAllPlatform,
            externalConversationId,
            fileType,
          }).then((res) => {
            if (storeId && !res.resourceCentreId) {
              uploadLogInfoMap[res.resourceId] = {
                size: file.size,
                type: file.type,
                region,
                countryCode: getCountryCode(),
                getDeviceInfoDuration,
                startTime,
                getHashTime,
                getPreUrlTime,
                uploadOssTime,
                getHashDuration,
                getPreUrlDuration,
                uploadOssDuration,
              };
            }
            return res;
          });
        });
      });
    })
    .catch((e) => {
      logger.info(
        `[UploadOssError]: ${e}\n${JSON.stringify({
          beforeTime,
          startTime,
          getHashTime,
          getPreUrlTime,
          uploadOssTime,
          fileSize: file.size,
        })}`,
      );
      return Promise.reject(e);
    });
};
